import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "./LicenseType.css";
import {
  hasUserAccess,
  getUserRole,
} from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../../shared/PageLoader";
import { useHistory } from "raviger";
import Table, {
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../common/Table";
import {
  AUDIT_STATUS,
  MONTHS_ENUM,
} from "../../../common/Constants";
import {preventDefault} from "../../../../utils/common";
import LicenseTypeDetail from "./LicenseTypeDetail";
import { useGetLicenseTypes } from "../../../../backend/license";
import LicenseFilters from "../common/LicenseFilters";
import { useSidenav } from "../../../../contexts/SidenavContext";

import AddNewLicenseTypePanel from "./AddNewLicenseTypePanel";

const SortFields: any = {
  "act.name": "actn_ame",
  "rule.name": "rule_name",
  "activity.name": "activity_name",
  "activity.type": "activity_activity_type",
  "associate_company.name": "associate_company_name",
  "location.name": "location_name",
};

function getAdvanceSearch(state: any) {
  const filters = [];
  if ((state || {}).fromDate) {
    filters.push({
      columnName: "fromDate",
      value: dayjs(state.fromDate).startOf("D").toISOString(),
    });
    filters.push({
      columnName: "toDate",
      value: dayjs(state.toDate).endOf("D").toISOString(),
    });
  }

  if ((state || {}).auditType) {
    filters.push({ columnName: "auditType", value: state.auditType });
  }
  return filters.length > 0 ? filters : undefined;
}

function LicenseType() {
  const { state }: any = useHistory();
  const { toggleSidenav } = useSidenav();
  const [checkedStatuses, setCheckedStatuses] = useState<any>(
    (state || {}).status ? { [state.status]: true } : {}
  );
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>();
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const [advaceSearchFilters, setAdvanceSearchFilters] = useState(
    getAdvanceSearch(state)
  );
  const afRef: any = useRef();
  afRef.current = advaceSearchFilters;
  const [statusFilters, setStatusFilters] = useState<any>();
  const sfRef = useRef();
  sfRef.current = statusFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef();
  payloadRef.current = payload;
  const { licenseTypes, total, isFetching, refetch, companyIds } = useGetLicenseTypes(
   payload,
   Boolean(payload)
  );

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [previousFilters, setPreviousFilters] = useState<any>({});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isLicenseDetailOpen, setLicenseDetailOpen] = useState(false);
  const [selectedLicenseType, setSelectedLicenseType] = useState<any>(null);

  const onLocationChange= useCallback((filters: any) =>{
    setLocationFilter(filters);
    const _params = {                                                                                                 
          ...params,                                                                                                      
          page: 1,                                                                                                        
          filters: {                                                                                                      
            // ...filters,                                                                                                   
            ...(sfRef.current || {}),                                                                                     
          },                                                                                                              
        };                                                                                                                
        setParams(_params);                                                                                               
        setPayload(_params);                                                                                              
        setPreviousFilters(filters);                                                                                      
    }, [params]);

  // Initialize payload on mount to load all license types
  useEffect(() => {
    const initialPayload = {
      ...DEFAULT_PAYLOAD,
      page: 1,
      per: 10,
          include: ["company", "associate_company", "state", "city"],
          includes: ["company", "associate_company", "state", "city"],
          filters: {},
          sorts: ["created_at desc"]
        };
        setParams(initialPayload);
        setPayload(initialPayload);
      }, []); 

  useEffect(() => {
    checkFirstTimePublish();
  }, [licenseTypes]);

  function checkFirstTimePublish() {
    const { month_eq, year_eq } = getAdvanceSearchPayload();
    if (month_eq && year_eq) {
      const _filter = {
        ...previousFilters,
      };
      const date = new Date();
      date.setDate(1);
      date.setFullYear(parseInt(`${year_eq}`));
      date.setMonth(MONTHS_ENUM.indexOf(month_eq));
      _filter["monthYear"] = date;
      setPreviousFilters(_filter);
    }
  }

  function ActiveStatusTmpl({ cell }: any) {
    const isActive = cell.getValue();

    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
        {isActive ? 'Yes' : 'No'}
      </span>
    );
  }

  function StateCityTmpl({ cell }: any) {
    const value = cell.getValue();
    const row = cell.getData();

    // Check if both state and city are empty
    const hasState = row.state && row.state.name;
    const hasCity = row.city && row.city.name;

    if (!hasState && !hasCity) {
      return <span>Global</span>;
    }

    return <span>{value || '-'}</span>;
  }

  function ActionsTmpl({ cell }: any) {
    const status = cell.getValue();
    const row = cell.getData();
    const color = {
      [AUDIT_STATUS.COMPLIANT]: "status-compliant",
      [AUDIT_STATUS.NON_COMPLIANCE]: "status-non-compliance",
      [AUDIT_STATUS.NOT_APPLICABLE]: "status-not-applicable",
    };
    return (
      <div className="cursor-pointer">
        <div onClick={() => {
          toggleSidenav(false);
          setSelectedLicenseType(row);
          setLicenseDetailOpen(true);
        }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path d="M0.666626 8.65627C0.666626 8.65627 3.33329 3.32294 7.99996 3.32294C12.6666 3.32294 15.3333 8.65627 15.3333 8.65627C15.3333 8.65627 12.6666 13.9896 7.99996 13.9896C3.33329 13.9896 0.666626 8.65627 0.666626 8.65627Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.99996 10.6563C9.10453 10.6563 9.99996 9.76084 9.99996 8.65627C9.99996 7.5517 9.10453 6.65627 7.99996 6.65627C6.89539 6.65627 5.99996 7.5517 5.99996 8.65627C5.99996 9.76084 6.89539 10.6563 7.99996 10.6563Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
      </div>
    );
  }

  function DateTmpl({ cell }: any) {
    const value = cell.getValue();
    return value ? dayjs(value).format("DD-MM-YYYY") : "-";
  }

  const columns = [
    {
      title: "IDs",  // license Type id
      field: "id",
      //width: 150,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "License Type", // license type name
      field: "name",
      //width: 150,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "Company",
      field: "company.name",
      //width: 150,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company.name",
      //width: 150,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "State",
      field: "state.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      //width: 150,
    },
    {
      title: "City",
      field: "city.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      //width: 150,
    },
    {
      title: "Created on",
      field: "created_at",
      formatter: reactFormatter(<DateTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      //width: 150,
    },
    {
      title: "Active",
      field: "is_active",
      formatter: reactFormatter(<ActiveStatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      //width: 150,
    },
    {
      title: "Actions",
      field: "actions",
      //width: 150,
      formatter: reactFormatter(<ActionsTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
  ];

  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: "auto",
    selectable: getUserRole() === "License Manager" ? true : false,
    paginate: true,
    // initialSort: ["month desc"],
    rowFormatter,
  });

  function rowFormatter(row: any) {
    const data = row.getData();
    const element = row.getElement();
  }

  function formatApiResponse(params: any, list: any, totalRecords: number) {
    const { page, per } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (per || 1)) || 1,
      page: page || 1,
    };
    return tdata;
  }

  function hasFilters(ref: any, field = "company_id_eq") {
    const _filters: any =
      (ref
        ? ref.current
        : ({ ...(payloadRef.current || {}) } as any)["filters"]) || {};
    return _filters[field];
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {                                                      
    const { field, dir } = (params.sort || [])[0] || {};
    const _params = {
      per: params.size,
      page: params.page,
      sorts: [`${field || "created_at"} ${dir || "desc"}`],
      include: [                                                                                                     
        "company",                                                                                                   
        "associate_company",                                                                                         
        "state",                                                                                                     
        "city"                                                                                                       
      ],                                                                                                             
      includes: [                                                                                                    
        "company",                                                                                                   
        "associate_company",                                                                                         
        "state",                                                                                                     
        "city"                                                                                                       
      ],                                                                                                             
    };                                                                                                               
    setParams(_params);                                                                                              
    setPayload({                                                                                                     
      ...DEFAULT_PAYLOAD,                                                                                            
      ..._params,                                                                                                    
      filters: {                                                                                                     
        ...(lfRef.current || {}),                                                                                    
        ...(sfRef.current || {}),                                                                                    
      },                                                                                                             
    });                                                                                                              
    return Promise.resolve(formatApiResponse(params, licenseTypes || [], total || 0));                               
  }              

  function handlePageNav(_pagination: any) {
    const _params = {
      ...params,
      ..._pagination,
      filters: {
        ...(lfRef.current || {}),
        ...(sfRef.current || {}),
      }
    };
    setParams({ ..._params });
    setPayload({ ..._params });
  }

  function getAdvanceSearchPayload() {
    return { ...(afRef.current || {}) };
  }

  useEffect(() => {
    if (locationFilters) {
      const _params = { ...params, page: 1 };
      setParams({ ..._params });
      setPayload({
        ...payload,
        ...DEFAULT_PAYLOAD,
        // sorts: ["month desc"],
        ..._params,
        filters: {
          ...locationFilters,
          ...(afRef.current || {}),
          ...(sfRef.current || {}),
        },
      });
    }
  }, [locationFilters]);

  useEffect(() => {
    if (locationFilters) {
      const _params = { ...params, page: 1 };
      setParams({ ..._params });
      setPayload({
        ...payload,
        ...DEFAULT_PAYLOAD,
        // sorts: ["month desc"],
        ..._params,
        filters: {
          ...(lfRef.current || {}),
          ...(advaceSearchFilters || {}),
          ...(sfRef.current || {}),
        },
      });
    }
  }, [advaceSearchFilters]);

  useEffect(() => {
    if (locationFilters) {
      const _params = { ...params, page: 1 };
      setParams({ ..._params });
      setPayload({
        ...payload,
        ...DEFAULT_PAYLOAD,
        // sorts: ["month desc"],
        ..._params,
        filters: {
          ...(lfRef.current || {}),
          ...(afRef.current || {}),
          ...(statusFilters || {}),
        },
      });
    }
  }, [statusFilters]);

  useEffect(() => {
    if (checkedStatuses) {
      const keys = Object.keys(checkedStatuses);
      const result = keys.filter((key) => !!checkedStatuses[key]);
      const _result: any = {};
      if (result.length > 0) {
        _result["status_in"] = result;
      }
      setStatusFilters(_result);
    }
  }, [checkedStatuses]);

  useEffect(() => {
    if (!isFetching && payload) {
      setSelectedRows([]);
      setData(formatApiResponse(params, licenseTypes || [], total || 0));
    }
  }, [isFetching]);

  return (
    <>
      <div className="licensetype-wrapper">
        <div className="d-flex flex-column license-dashboard">
          <div className="d-flex p-3 align-items-center pageHeading">
            <div>
              <h4 className="mb-0">License types</h4>
              <p>
                Define custom license types for licenses applications. 
              </p>
            </div>
            <div className="d-flex align-items-end h-100">
              {hasUserAccess(USER_PRIVILEGES.ADD_LICENSE_TYPE) && (
                <button className="button-primary" onClick={() => {
                  toggleSidenav(false);
                  setIsSidePanelOpen(true);
                }}>Create License Type</button>
              )}
            </div>
          </div>

          <form className="p-0 mx-3">
            <div className="card border-0 py-4 px-2">
              <div className="d-flex flex-row filters">
                <LicenseFilters onChange={onLocationChange} showStateCityInsteadOfLocation/>
              </div>
            </div>
          </form>
          <div className="position-relative p-3 pb-0 license-table">
            <Table
              data={data}
              options={tableConfig}
              isLoading={isFetching}
              onSelectionChange={setSelectedRows}
              onPageNav={handlePageNav}
            />
          </div>
        </div>
        {/* {(submitting) && <PageLoader />} */}
        <AddNewLicenseTypePanel
          isOpen={isSidePanelOpen}
          closePanel={() => setIsSidePanelOpen(false)}
          onSuccess={() => {                                                                                                
            refetch();                                                                                                      
            setIsSidePanelOpen(false);  
          }}
        />
        <LicenseTypeDetail
          isOpen={isLicenseDetailOpen}
          closePanel={() => {
            setLicenseDetailOpen(false);
            setSelectedLicenseType(null);
          }}
          license={selectedLicenseType}
          refetch={refetch}
        />
      </div>
      {isFetching && <PageLoader />}
    </>
  );
}

export default LicenseType;
